import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceClaimListItem } from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceClaimsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type ClaimSortField = 'claimNumber' | 'policyNumber' | 'lineOfBusiness' | 'reserveAmount' | 'openedAtUtc' | 'status';

@Component({
  standalone: true,
  selector: 'ain-insurance-claims-shell',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    FormsModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.claims.title')"
      [description]="t('insurance.claims.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.claims.help.page.title'"
        [descriptionKey]="'insurance.claims.help.page.description'"
        [bulletKeys]="[
          'insurance.claims.help.page.bullets.scope',
          'insurance.claims.help.page.bullets.fnol',
          'insurance.claims.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.actions.createClaim')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.claims.help.listFilters.title'"
                [descriptionKey]="'insurance.claims.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.claims.help.listFilters.bullets.search',
                  'insurance.claims.help.listFilters.bullets.status',
                  'insurance.claims.help.listFilters.bullets.dates'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.filters.searchClaim') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.claims.filters.status') }}</span>
                <p-select
                  [options]="claimStatusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.claims.filters.lineOfBusiness') }}</span>
                <input pInputText [(ngModel)]="lineOfBusinessFilter" name="lineOfBusinessFilter" />
              </label>
              <label>
                <span>{{ t('insurance.claims.filters.fromDate') }}</span>
                <ain-date-input [(ngModel)]="fromDateFilter" name="fromDateFilter" />
              </label>
              <label>
                <span>{{ t('insurance.claims.filters.toDate') }}</span>
                <ain-date-input [(ngModel)]="toDateFilter" name="toDateFilter" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.states.emptyClaims')" [message]="t('insurance.states.emptyClaimsMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('claimNumber')">{{ t('insurance.claims.fields.claimNumber') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('policyNumber')">{{ t('insurance.claims.fields.policyNumber') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('lineOfBusiness')">{{ t('insurance.claims.fields.lineOfBusiness') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('reserveAmount')">{{ t('insurance.claims.fields.reserve') }}</button></th>
                  <th>{{ t('insurance.claims.fields.paid') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('openedAtUtc')">{{ t('insurance.claims.fields.openedAt') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.claimNumber }}</button></td>
                  <td>{{ row.policyNumber }}</td>
                  <td>{{ row.lineOfBusiness }}</td>
                  <td>{{ row.reserveAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.paidAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.openedAtUtc | ainDate }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceClaimsShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceClaimListItem[]>([]);
  readonly selectedRows = signal<InsuranceClaimListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<ClaimSortField>('claimNumber');
  readonly sortOrder = signal<SortOrder>(1);
  statusFilter: string | null = null;
  lineOfBusinessFilter = '';
  fromDateFilter: string | null = null;
  toDateFilter: string | null = null;

  liveStatusKey = liveStatusKey;

  readonly claimStatusOptions = [
    { value: 'Reported', labelKey: 'insurance.claims.status.reported' },
    { value: 'Open', labelKey: 'insurance.claims.status.open' },
    { value: 'UnderInvestigation', labelKey: 'insurance.claims.status.underInvestigation' },
    { value: 'Approved', labelKey: 'insurance.claims.status.approved' },
    { value: 'Declined', labelKey: 'insurance.claims.status.declined' },
    { value: 'PartiallyPaid', labelKey: 'insurance.claims.status.partiallyPaid' },
    { value: 'Paid', labelKey: 'insurance.claims.status.paid' },
    { value: 'Closed', labelKey: 'insurance.claims.status.closed' },
    { value: 'Reopened', labelKey: 'insurance.claims.status.reopened' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listClaims().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceClaimListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = this.rows()
      .filter(row => !value
        || row.claimNumber.toLowerCase().includes(value)
        || row.policyNumber.toLowerCase().includes(value))
      .filter(row => !this.statusFilter || row.status === this.statusFilter)
      .filter(row => !this.lineOfBusinessFilter?.trim()
        || row.lineOfBusiness.toLowerCase().includes(this.lineOfBusinessFilter.trim().toLowerCase()))
      .filter(row => !this.fromDateFilter || new Date(row.openedAtUtc) >= new Date(this.fromDateFilter))
      .filter(row => !this.toDateFilter || new Date(row.openedAtUtc) <= new Date(this.toDateFilter));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceClaimListItem[] | InsuranceClaimListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/claims', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/claims/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-claims.csv',
      ['ClaimNumber', 'PolicyNumber', 'LineOfBusiness', 'Status', 'ReserveAmount', 'PaidAmount', 'CurrencyCode', 'OpenedAtUtc'],
      rows.map(item => [
        item.claimNumber,
        item.policyNumber,
        item.lineOfBusiness,
        item.status,
        item.reserveAmount.toString(),
        item.paidAmount.toString(),
        item.currencyCode,
        item.openedAtUtc
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: ClaimSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  statusKey(status: InsuranceClaimListItem['status']): string {
    return `insurance.claims.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceClaimsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
